import { Suspense, lazy, useEffect, useRef, useState } from "react";

import { FieldHelperText, type FieldProps } from "@firecms/core";

import type { RichTextStrings } from "../components/editor/strings";

/**
 * Split out of the initial bundle, because this is the only place in the CMS
 * that needs Lexical and most sessions never reach it.
 *
 * `App.tsx` imports the collections eagerly to build the navigation, so a
 * static import here would put the editor — 336 kB, 108 kB gzipped — in the
 * chunk that the login view and the collection tables also wait for. It is only
 * ever rendered on the news *entity* form.
 *
 * The import has to be a bare dynamic `import()` of a module that does nothing
 * at load time beyond defining components; anything with a side effect on
 * import would run on first keystroke into the form instead of at startup,
 * which is harder to reason about, not easier.
 *
 * Only the type is imported statically above, and `import type` is erased — so
 * this file stays free of Lexical. Check that with a build: the main chunk
 * should be ~4,386 kB, with the editor in a chunk of its own.
 */
const LexicalEditor = lazy(() =>
    import("../components/editor/LexicalEditor").then((module) => ({
        default: module.LexicalEditor,
    })),
);

export interface RichTextFieldProps extends RichTextStrings {
    /** Shown on the empty canvas. */
    placeholder: string;
}

/**
 * Stands in while the editor chunk loads.
 *
 * Deliberately the same shape and height as the real editor — the toolbar bar
 * plus a `min-h-60` body — because the form has two of these stacked and a
 * shorter placeholder would let everything below jump once the chunk arrives.
 * Carries no text, which keeps it out of the localisation surface for a state
 * that lasts a few hundred milliseconds once and is then cached.
 */
function EditorSkeleton() {
    return (
        <div
            aria-busy
            className="rounded-md border border-surface-200 dark:border-surface-700"
        >
            <div className="h-10 animate-pulse rounded-t-md border-b border-surface-200 bg-surface-50 dark:border-surface-700 dark:bg-surface-900" />
            <div className="min-h-60" />
        </div>
    );
}

/**
 * The article body: a Lexical editor whose value is HTML.
 *
 * **Why not FireCMS's own markdown field.** `StringProperty.markdown` gives a
 * ProseMirror editor that stores a markdown string, which was what this field
 * used until 2026-09-07. Two things pushed it out. Editors were writing prose
 * in a surface that showed markdown mechanics rather than the article, and
 * embeds had to be typed as raw HTML — the reason `MarkdownConfig.html` was
 * left on, and the reason the site's sanitiser was the only guard. Lexical
 * makes an image or a video a node an editor can see, select and delete, and
 * an unsupported provider now fails at paste time in front of the person who
 * pasted it.
 *
 * **The stored shape changed with it: `body.{zh,jp}` holds HTML, not markdown.**
 * `ArticleSchema.body` is still `LocalizedTextSchema` because it was always
 * just a string, so nothing in the schema needed to move — but
 * `apps/one-ai/src/lib/cms/html.ts` no longer runs a markdown parser, and
 * `editorNodes` in `components/editor/html.ts` is the contract between what
 * this field emits and what that sanitiser allows through.
 *
 * See `apps/one-ai/src/spec/publish-and-rebuild.md`, invariant 7.
 */
export function RichTextField({
    property,
    value,
    setValue,
    error,
    showError,
    disabled,
    isSubmitting,
    includeDescription,
    underlyingValueHasChanged,
    customProps,
}: FieldProps<string, RichTextFieldProps>) {
    /**
     * Bumping this reloads the stored value into the editor. It starts at 0 for
     * the mount-time load and moves only when the datasource changed underneath
     * us — never in response to our own `setValue`, which would reimport the
     * document on every keystroke and send the caret to the top.
     */
    const [revision, setRevision] = useState(0);
    const lastPushed = useRef<string | null>(null);

    useEffect(() => {
        if (!underlyingValueHasChanged) return;
        // Our own echo: FireCMS reports the value it just took from us as
        // changed too, and reloading on that is exactly the loop above.
        if ((value ?? "") === (lastPushed.current ?? "")) return;
        setRevision((current) => current + 1);
    }, [underlyingValueHasChanged, value]);

    const { placeholder, ...strings } = customProps;

    return (
        <>
            <p className="mb-1 text-sm font-medium text-surface-700 dark:text-surface-300">
                {property.name}
            </p>

            <Suspense fallback={<EditorSkeleton />}>
                <LexicalEditor
                    value={value ?? ""}
                    revision={revision}
                    disabled={Boolean(disabled) || Boolean(isSubmitting)}
                    placeholder={placeholder}
                    strings={strings}
                    onChange={(html) => {
                        // `null` rather than `""` for an empty body: the
                        // property is `required`, and Yup's `required` rejects
                        // null but accepts an empty string — so returning ""
                        // would let an article save with no body at all.
                        lastPushed.current = html;
                        setValue(html.trim() ? html : null);
                    }}
                />
            </Suspense>

            <FieldHelperText
                includeDescription={includeDescription}
                showError={showError}
                error={error}
                property={property}
            />
        </>
    );
}
